// Hotel booking wizard — OTA-standard multi-step checkout.
//
// Step 1: Guest details    (title, first, last, DOB, contact, saved travellers autofill)
// Step 2: Review & pay     (final summary, gateway selection, Razorpay)
//
// Data-integrity policy: the entire booking summary is driven by
// `location.state.item`, which was constructed from the SELECTED rate on the
// hotel-details page. No fabricated add-ons, no invented pricing.
//
// The Razorpay + booking-confirm plumbing mirrors BookingDetails.jsx so we
// don't fork the tested payment path.

import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import {
  Hotel, User, CreditCard, CheckCircle, Loader2, ChevronRight, ChevronLeft,
  ShieldCheck, MapPin, Calendar, Users, Check,
} from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { useToast } from '../../hooks/use-toast';
import { api, formatApiErrorDetail } from '../../lib/api';
import { openRazorpayCheckout } from '../../lib/razorpay';
import { track, Events } from '../../lib/analytics';
import { useCurrency } from '../../context/CurrencyContext';
import { useCustomerAuth } from '../../context/CustomerAuthContext';
import SavedTravellerPicker from '../account/SavedTravellerPicker';
import CheckoutGatewaySelector from '../checkout/CheckoutGatewaySelector';
import NoImagePlaceholder from './NoImagePlaceholder';

// Same helpers used by HotelDetailPage — kept inline so this file has
// zero fabricated data.
const MEAL_LABELS = {
  breakfast: 'Breakfast included',
  'breakfast-buffet': 'Breakfast (buffet)',
  'half-board': 'Half board',
  'full-board': 'Full board',
  'all-inclusive': 'All inclusive',
  'ultra-all-inclusive': 'Ultra all-inclusive',
  'continental-breakfast': 'Continental breakfast',
  'american-breakfast': 'American breakfast',
  dinner: 'Dinner included',
};
const mealLabel = (raw) => {
  if (!raw) return '';
  const key = String(raw).toLowerCase();
  if (key === 'nomeal' || key === 'no-meal' || key === 'ro') return 'Room only';
  return MEAL_LABELS[key] || '';
};
const fmtCancelDeadline = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return ''; }
};

const STEPS = [
  { key: 'guest', label: 'Guest details', icon: User },
  { key: 'pay', label: 'Review & pay', icon: CreditCard },
];

const emptyForm = {
  title: 'Mr',
  first_name: '',
  last_name: '',
  dob: '',
  gender: '',
  email: '',
  phone: '',
  special_request: '',
};

const HotelBookingCheckout = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { formatPrice } = useCurrency();
  const { customer } = useCustomerAuth();

  const [item, setItem] = useState(location.state?.item || null);
  const [loading, setLoading] = useState(!item);
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(emptyForm);
  const [gateway, setGateway] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (item) { setLoading(false); return; }
    // Deep-link fallback — no item snapshot in state. Route the user back to
    // the details page so they can re-select a rate.
    setLoading(false);
  }, [item]);

  // Prefill contact from logged-in customer profile.
  useEffect(() => {
    if (!customer) return;
    setForm((f) => ({
      ...f,
      first_name: f.first_name || (customer.name || '').split(' ')[0] || '',
      last_name: f.last_name || (customer.name || '').split(' ').slice(1).join(' ') || '',
      email: f.email || customer.email || '',
      phone: f.phone || customer.phone || '',
    }));
  }, [customer]);

  const nights = Number(item?.__nights) || 1;
  const rooms = Number(item?.rooms || 1);
  const guestsCount = Number(item?.guests || 2);
  // The selling total was forwarded from the details page with the SELECTED
  // rate baked in. If for any reason it isn't present, derive it from the
  // per-night selling rate × nights — never from a stale cheapest.
  const roomTotal = Number(
    item?.selling_total
    ?? (item?.display_price && !item?.__nights ? item.display_price : null)
    ?? (item?.display_price ? item.display_price * nights : null)
    ?? (item?.net_price_per_night ? item.net_price_per_night * nights : 0),
  ) || 0;
  const grandTotal = roomTotal;

  const stepValid = useMemo(() => {
    if (step === 0) {
      return !!(form.first_name.trim() && form.last_name.trim() && form.email.trim() && /^\S+@\S+\.\S+$/.test(form.email) && form.phone.trim().length >= 8);
    }
    return true;
  }, [step, form]);

  const pick = (t) => setForm((f) => ({
    ...f,
    title: t.title || f.title,
    first_name: (t.full_name || '').split(' ')[0] || f.first_name,
    last_name: (t.full_name || '').split(' ').slice(1).join(' ') || f.last_name,
    dob: t.dob || f.dob,
    gender: t.gender || f.gender,
    email: t.email || f.email,
    phone: t.phone || f.phone,
  }));

  const handleField = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const goNext = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const goBack = () => setStep((s) => Math.max(0, s - 1));

  const confirmAndPay = async () => {
    if (!item) return;
    setSubmitting(true);
    try {
      const created = await api.post('/v2/bookings/create', {
        service: 'hotels',
        item_id: id,
        passengers: [{
          title: form.title || 'Mr',
          first_name: form.first_name,
          last_name: form.last_name,
          dob: form.dob || null,
          gender: form.gender || null,
        }],
        contact: { name: `${form.first_name} ${form.last_name}`.trim(), email: form.email, phone: form.phone },
        units: 1,
        tier: 'b2c',
        // Full snapshot of the SELECTED rate — the backend re-verifies
        // and rebuilds prebook/booking payloads from this.
        item_snapshot: item,
        extras: {
          nights, rooms, guests: guestsCount,
          rate_key: item.rate_key,
          book_hash: item.book_hash,
          match_hash: item.match_hash,
          special_request: form.special_request || null,
        },
      });

      track(Events.PAYMENT_INITIATED, { booking_id: created.data.id, amount: created.data.total_amount, service: 'hotels' });
      let payment;
      try {
        payment = await openRazorpayCheckout({
          orderId: created.data.razorpay_order_id,
          amount: Math.round(created.data.total_amount * 100),
          keyId: created.data.razorpay_key_id,
          description: `Hotel booking ${created.data.id}`,
          prefill: {
            name: `${form.first_name} ${form.last_name}`,
            email: form.email,
            contact: form.phone,
          },
        });
      } catch (err) {
        toast({
          title: err.code === 'CANCELLED' ? 'Payment cancelled' : 'Payment failed',
          description: err.code === 'CANCELLED'
            ? 'You can retry from My Bookings.'
            : (err.message || 'Try a different payment method.'),
          variant: 'destructive',
        });
        return;
      }

      const confirmed = await api.post('/v2/bookings/confirm', {
        booking_id: created.data.id,
        razorpay_order_id: payment.razorpay_order_id,
        razorpay_payment_id: payment.razorpay_payment_id,
        razorpay_signature: payment.razorpay_signature,
      });
      track(Events.PAYMENT_COMPLETED, { booking_id: created.data.id, amount: created.data.total_amount });
      track(Events.BOOKING_CONFIRMED, { booking_id: created.data.id, service: 'hotels', amount: created.data.total_amount });

      toast({
        title: 'Booking Confirmed',
        description: `Voucher ${confirmed.data.provider_confirmation?.voucher || confirmed.data.id}. Total ${formatPrice(confirmed.data.total_amount)}`,
      });
      setTimeout(() => navigate(`/account/bookings/${created.data.id}`), 1500);
    } catch (err) {
      toast({
        title: 'Booking Failed',
        description: formatApiErrorDetail(err.response?.data?.detail) || 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-gray-50 dark:bg-gray-950">
        <Loader2 className="h-8 w-8 animate-spin text-red-600" />
      </div>
    );
  }

  if (!item) {
    return (
      <div className="max-w-2xl mx-auto py-20 px-4 text-center">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Booking session expired</h1>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          Please re-select a room on the hotel details page — live prices need to be re-checked.
        </p>
        <Link to={`/hotels/${id}`} className="text-red-600 font-semibold hover:underline">Back to hotel</Link>
      </div>
    );
  }

  const showFreeCancel = item.has_free_cancellation === true && !!item.free_cancellation_before;
  const meal = mealLabel(item.meal || item.board_name || '');

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-red-700 to-red-900 text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center gap-3">
            <div className="bg-white/20 p-2.5 rounded-lg"><Hotel className="h-5 w-5" /></div>
            <div>
              <p className="text-white/80 text-xs uppercase tracking-widest">Complete your booking</p>
              <h1 className="text-xl sm:text-2xl font-bold">{item.name}</h1>
            </div>
          </div>
        </div>
      </div>

      {/* Stepper */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
        <ol className="flex items-center gap-2 sm:gap-4 mb-6" data-testid="checkout-stepper">
          {STEPS.map((s, i) => {
            const active = i === step;
            const done = i < step;
            const Icon = s.icon;
            return (
              <li key={s.key} className="flex items-center gap-2 flex-1 min-w-0">
                <div className={`h-8 w-8 rounded-full grid place-items-center text-xs font-bold transition ${done ? 'bg-emerald-600 text-white' : active ? 'bg-red-600 text-white' : 'bg-gray-200 dark:bg-gray-800 text-gray-500'}`}>
                  {done ? <CheckCircle className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
                </div>
                <span className={`text-sm font-medium truncate ${active ? 'text-gray-900 dark:text-white' : 'text-gray-500'}`}>{s.label}</span>
                {i < STEPS.length - 1 && <div className="hidden sm:block flex-1 h-px bg-gray-300 dark:bg-gray-700" />}
              </li>
            );
          })}
        </ol>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6">
        {/* MAIN */}
        <div className="min-w-0">
          <Card>
            <CardContent className="p-5 sm:p-6">

              {/* STEP 1 — GUEST DETAILS */}
              {step === 0 && (
                <div className="space-y-5" data-testid="step-guest">
                  <div>
                    <h2 className="text-lg font-bold text-gray-900 dark:text-white">Primary guest</h2>
                    <p className="text-xs text-gray-500 mt-0.5">This is who the room will be booked under. Government-issued ID must match at check-in.</p>
                  </div>
                  {customer && <SavedTravellerPicker onPick={pick} />}
                  <div className="grid grid-cols-[110px_1fr_1fr] gap-3">
                    <div>
                      <Label className="text-xs">Title</Label>
                      <select
                        name="title"
                        value={form.title}
                        onChange={handleField}
                        data-testid="input-title"
                        className="mt-1 h-10 w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 text-sm"
                      >
                        {['Mr', 'Ms', 'Mrs', 'Dr'].map((t) => <option key={t}>{t}</option>)}
                      </select>
                    </div>
                    <div>
                      <Label className="text-xs">First name *</Label>
                      <Input name="first_name" value={form.first_name} onChange={handleField} data-testid="input-first-name" placeholder="John" className="mt-1" required />
                    </div>
                    <div>
                      <Label className="text-xs">Last name *</Label>
                      <Input name="last_name" value={form.last_name} onChange={handleField} data-testid="input-last-name" placeholder="Doe" className="mt-1" required />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Email *</Label>
                      <Input name="email" type="email" value={form.email} onChange={handleField} data-testid="input-email" placeholder="you@example.com" className="mt-1" required />
                      <p className="text-[11px] text-gray-500 mt-1">Your voucher and confirmation will be sent here.</p>
                    </div>
                    <div>
                      <Label className="text-xs">Mobile *</Label>
                      <Input name="phone" type="tel" value={form.phone} onChange={handleField} data-testid="input-phone" placeholder="+91 98765 43210" className="mt-1" required />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Special request (optional)</Label>
                    <Input
                      name="special_request"
                      value={form.special_request}
                      onChange={handleField}
                      data-testid="input-special-request"
                      placeholder="High floor, twin beds, quiet room…"
                      className="mt-1"
                    />
                    <p className="text-[11px] text-gray-500 mt-1">We&apos;ll share this with the hotel — not guaranteed but often granted.</p>
                  </div>
                </div>
              )}

              {/* STEP 2 — REVIEW & PAY */}
              {step === 1 && (
                <div className="space-y-5" data-testid="step-pay">
                  <div>
                    <h2 className="text-lg font-bold text-gray-900 dark:text-white">Review & pay</h2>
                    <p className="text-xs text-gray-500 mt-0.5">One last look before we confirm your booking.</p>
                  </div>
                  <div className="p-4 rounded-xl bg-gray-50 dark:bg-gray-900/50 space-y-1.5 text-sm">
                    <div className="flex justify-between text-gray-700 dark:text-gray-300">
                      <span>Guest</span>
                      <span className="font-medium">{form.title} {form.first_name} {form.last_name}</span>
                    </div>
                    <div className="flex justify-between text-gray-700 dark:text-gray-300"><span>Email</span><span className="font-medium truncate max-w-[220px]">{form.email}</span></div>
                    <div className="flex justify-between text-gray-700 dark:text-gray-300"><span>Mobile</span><span className="font-medium">{form.phone}</span></div>
                    {item.room_type && (
                      <div className="flex justify-between text-gray-700 dark:text-gray-300">
                        <span>Room</span>
                        <span className="font-medium truncate max-w-[220px] text-right">{item.room_type}</span>
                      </div>
                    )}
                    {meal && (
                      <div className="flex justify-between text-gray-700 dark:text-gray-300">
                        <span>Meal</span>
                        <span className="font-medium">{meal}</span>
                      </div>
                    )}
                  </div>
                  <CheckoutGatewaySelector
                    value={gateway}
                    onChange={setGateway}
                    fallback={(
                      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-gray-700 dark:text-gray-200 flex items-start gap-2">
                        <ShieldCheck className="h-4 w-4 text-emerald-600 mt-0.5" />
                        <div>
                          256-bit secure payment via Razorpay.<br />
                          <span className="text-xs text-gray-500">Cards · UPI · Netbanking · Wallets</span>
                        </div>
                      </div>
                    )}
                  />
                </div>
              )}

              {/* Nav */}
              <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200 dark:border-gray-800">
                <Button
                  variant="ghost"
                  onClick={goBack}
                  disabled={step === 0 || submitting}
                  data-testid="checkout-back"
                  className="text-gray-700 dark:text-gray-300"
                >
                  <ChevronLeft className="h-4 w-4 mr-1" /> Back
                </Button>
                {step < STEPS.length - 1 ? (
                  <Button
                    onClick={goNext}
                    disabled={!stepValid}
                    data-testid="checkout-next"
                    className="bg-red-600 hover:bg-red-700 text-white"
                  >
                    Continue <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                ) : (
                  <Button
                    onClick={confirmAndPay}
                    disabled={submitting || !stepValid}
                    data-testid="checkout-pay"
                    className="bg-red-600 hover:bg-red-700 text-white min-w-[180px]"
                  >
                    {submitting
                      ? <Loader2 className="h-4 w-4 animate-spin" />
                      : `Pay ${formatPrice(grandTotal)}`}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* SIDEBAR — booking summary */}
        <aside className="lg:sticky lg:top-24 self-start space-y-3">
          <Card>
            <CardContent className="p-4">
              <div className="flex gap-3">
                <div className="h-20 w-20 rounded-lg overflow-hidden shrink-0 bg-gray-100 dark:bg-gray-800">
                  {(item.images?.[0] || item.image) ? (
                    <img
                      src={item.images?.[0] || item.image}
                      alt=""
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <NoImagePlaceholder compact />
                  )}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-bold text-gray-900 dark:text-white truncate">{item.name}</p>
                  <p className="text-[11px] text-gray-500 flex items-center gap-1 truncate"><MapPin className="h-3 w-3" /> {[item.area, item.city].filter(Boolean).join(', ') || '—'}</p>
                </div>
              </div>
              <div className="mt-3 space-y-1.5 text-xs text-gray-700 dark:text-gray-300 border-t pt-3 border-gray-200 dark:border-gray-800">
                <div className="flex items-center gap-2"><Calendar className="h-3 w-3 text-gray-400" /> {item.check_in || '—'} → {item.check_out || '—'}</div>
                <div className="flex items-center gap-2"><Users className="h-3 w-3 text-gray-400" /> {rooms} room{rooms > 1 ? 's' : ''}, {guestsCount} guest{guestsCount > 1 ? 's' : ''} · {nights} night{nights > 1 ? 's' : ''}</div>
                {item.room_type && (
                  <div className="text-gray-500 truncate">
                    {item.room_type}{meal ? ` · ${meal}` : ''}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4 space-y-2 text-sm">
              <div className="flex justify-between text-gray-700 dark:text-gray-300">
                <span>Room total ({nights} night{nights > 1 ? 's' : ''})</span>
                <span>{formatPrice(roomTotal)}</span>
              </div>
              <div className="flex justify-between font-bold text-gray-900 dark:text-white pt-2 border-t border-gray-200 dark:border-gray-800" data-testid="checkout-total">
                <span>Total</span>
                <span>{formatPrice(grandTotal)}</span>
              </div>
              <p className="text-[11px] text-gray-500">incl. taxes &amp; fees</p>
              {showFreeCancel && (
                <p className="text-[11px] text-emerald-700 dark:text-emerald-400 flex items-center gap-1 mt-1">
                  <Check className="h-3 w-3" /> Free cancellation until {fmtCancelDeadline(item.free_cancellation_before)}
                </p>
              )}
              <p className="text-[11px] text-gray-500 flex items-center gap-1"><ShieldCheck className="h-3 w-3" /> Instant confirmation</p>
            </CardContent>
          </Card>
        </aside>
      </div>
    </div>
  );
};

export default HotelBookingCheckout;
